import React, { useState } from 'react';
import { X, AlertTriangle, CreditCard, Rocket } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { useSubscription, TIER_LABELS } from '../../contexts/SubscriptionContext';
import { useAuth } from '../../contexts/AuthContext';

/**
 * TrialBanner — shown above page content when subscription is in a non-active state.
 *
 * States covered:
 *   isTrialing        → countdown + "Manage subscription" link
 *   isPastDue         → payment failed + retry CTA
 *   isCancelled + trialExhausted → trial ended + upgrade CTA
 *
 * Hidden when: isActive, isLoading, or tier=free with !trialExhausted.
 * AC9: full-width, text wraps on mobile (flex-col sm:flex-row).
 */
const TrialBanner: React.FC = () => {
  const { isTrialing, isActive, isPastDue, isCancelled, trialExhausted,
    trialExpiresAt, plan, isLoading, isPlaceholderData } = useSubscription();
  const { user } = useAuth();
  const navigate = useNavigate();
  const [dismissed, setDismissed] = useState(false);

  // Org members are provisioned through their org — no trial or CC flow applies
  if (user?.org_id) return null;
  if (isLoading || isPlaceholderData || isActive || dismissed) return null;
  if (!isTrialing && !isPastDue && !(isCancelled && trialExhausted)) return null;

  const daysLeft = trialExpiresAt
    ? Math.max(0, Math.ceil((new Date(trialExpiresAt).getTime() - Date.now()) / 86_400_000))
    : 0;

  const chargeDate = trialExpiresAt
    ? new Date(trialExpiresAt).toLocaleDateString(undefined, { month: 'short', day: 'numeric', year: 'numeric' })
    : null;

  const planLabel = TIER_LABELS[plan ?? 'pro'] ?? 'Pro';

  // Determine variant
  let bgClass = '';
  let icon: React.ReactNode = null;
  let message: React.ReactNode = null;
  let cta: React.ReactNode = null;

  if (isTrialing) {
    bgClass = 'bg-blue-600/90';
    icon = <Rocket size={16} className="shrink-0" />;
    message = (
      <span>
        Your <strong>{planLabel}</strong> trial expires in <strong>{daysLeft} day{daysLeft !== 1 ? 's' : ''}</strong>.
        {chargeDate && <> Your card will be charged on <strong>{chargeDate}</strong>.</>}
      </span>
    );
    cta = (
      <button
        onClick={() => navigate('/upgrade')}
        className="text-white underline underline-offset-2 hover:no-underline font-medium whitespace-nowrap"
      >
        Manage subscription
      </button>
    );
  } else if (isPastDue) {
    bgClass = 'bg-red-600/90';
    icon = <CreditCard size={16} className="shrink-0" />;
    message = <span>Payment failed. Please update your payment method.</span>;
    cta = (
      <button
        onClick={() => navigate('/upgrade')}
        className="bg-white text-red-700 font-semibold px-3 py-1 rounded hover:bg-red-50 transition-colors whitespace-nowrap text-sm"
      >
        Retry payment
      </button>
    );
  } else if (isCancelled && trialExhausted) {
    bgClass = 'bg-amber-600/90';
    icon = <AlertTriangle size={16} className="shrink-0" />;
    message = (
      <span>
        Your trial has ended. Upgrade to continue accessing <strong>{planLabel}</strong> features.
      </span>
    );
    cta = (
      <button
        onClick={() => navigate('/upgrade')}
        className="bg-white text-amber-700 font-semibold px-3 py-1 rounded hover:bg-amber-50 transition-colors whitespace-nowrap text-sm"
      >
        Upgrade now
      </button>
    );
  }

  return (
    <div className={`${bgClass} text-white px-3 py-2 sm:px-6 sm:py-3`} role="alert">
      <div className="flex flex-col sm:flex-row sm:items-center gap-2 sm:gap-4 max-w-screen-xl mx-auto">
        <div className="flex items-center gap-2 flex-1 min-w-0">
          {icon}
          <span className="text-sm leading-snug">{message}</span>
        </div>
        <div className="flex items-center gap-3 shrink-0">
          {cta}
          <button
            onClick={() => setDismissed(true)}
            className="p-1 hover:bg-white/20 rounded transition-colors"
            aria-label="Dismiss"
          >
            <X size={14} />
          </button>
        </div>
      </div>
    </div>
  );
};

export default TrialBanner;
